// ─── Component Runtime ────────────────────────────────────────────────────────
//
// Phase 6e Layer. 3-state operational lifecycle authority for component
// instances within the Phase 3 runtime. Sole owner of mount/dispose operations.
//
// 3-state lifecycle (structural encoding):
//   created  - entry in registry, unsub=null (transient, never externally observable)
//   mounted  - entry in registry, unsub=fn (persistent; subscription held)
//   disposed - entry removed from registry
//
// Model B population: registry contains only currently mounted (active-page) components.
// RC-11: previous page fully disposed before next page is mounted.
//
// Cleanup chain position: clearRuntimeRegistry → clearBindingResolver
//
// Import path: @/components/componentRuntime

import { requireComponent } from '@/components/componentRegistry';
import {
  subscribeToComponent,
  getResolvedPropsForComponent,
  dispatchComponentEvent,
} from '@/components/bindingResolver';
import { eventBus } from '@/events/eventBus';
import { logger } from '@/system/logger';

// ─── Internal type ────────────────────────────────────────────────────────────

interface RuntimeEntry {
  unsub: (() => void) | null;
}

// ─── Implementation ───────────────────────────────────────────────────────────

class ComponentRuntimeImpl {
  private _registry      = new Map<string, RuntimeEntry>();
  private _pageMaps      = new Map<string, string[]>();
  private _currentPageId: string | null = null;
  private _eventUnsub:   (() => void) | null = null;

  initComponentRuntime(
    initialPageId: string,
    pageMaps: Array<{ pageId: string; componentIds: string[] }>,
  ): void {
    for (const { pageId, componentIds } of pageMaps) {
      this._pageMaps.set(pageId, componentIds);
    }

    this._currentPageId = initialPageId;

    for (const id of this._pageMaps.get(initialPageId) ?? []) {
      this.mountComponent(id);
    }

    // page:switched is the sole navigation signal (emitted only from Phase 8 app:navigate consumer).
    // Initial page is mounted synchronously above - no page:switched fires during boot.
    this._eventUnsub = eventBus.on('page:switched', (event) => {
      const oldPageId = this._currentPageId;

      // RC-11: fully dispose old page before mounting new page
      if (oldPageId !== null) {
        for (const id of this._pageMaps.get(oldPageId) ?? []) {
          if (this.isComponentMounted(id)) {
            this.disposeComponent(id);
          }
        }
      }

      this._currentPageId = event.pageId;

      const newIds = this._pageMaps.get(event.pageId);
      if (!newIds) {
        logger.warn(`[componentRuntime] page:switched - unknown pageId "${event.pageId}"`);
        return;
      }

      for (const id of newIds) {
        this.mountComponent(id);
      }
    });
  }

  clearRuntimeRegistry(): void {
    const ids = Array.from(this._registry.keys());
    for (const id of ids) {
      this.disposeComponent(id);
    }

    this._eventUnsub?.();
    this._eventUnsub = null;
    this._pageMaps.clear();
    this._currentPageId = null;
  }

  mountComponent(componentId: string): void {
    if (this._registry.has(componentId)) return;

    // created state - entry present, unsub=null
    const entry: RuntimeEntry = { unsub: null };
    this._registry.set(componentId, entry);

    try {
      requireComponent(componentId);
    } catch (e) {
      this._registry.delete(componentId);
      logger.error(`[componentRuntime] mountComponent - component not registered: "${componentId}"`, { error: e });
      return;
    }

    getResolvedPropsForComponent(componentId);

    // mounted state - entry present, unsub=fn
    entry.unsub = subscribeToComponent(componentId, () => {});
  }

  disposeComponent(componentId: string): void {
    const entry = this._registry.get(componentId);
    if (!entry) return;

    entry.unsub?.();
    this._registry.delete(componentId);
  }

  async handleEvent(componentId: string, eventName: string): Promise<void> {
    await dispatchComponentEvent(componentId, eventName);
  }

  isComponentMounted(componentId: string): boolean {
    return this._registry.has(componentId);
  }

  getMountedComponentIds(): string[] {
    return Array.from(this._registry.keys());
  }
}

const _componentRuntime = new ComponentRuntimeImpl();

export const initComponentRuntime   = _componentRuntime.initComponentRuntime.bind(_componentRuntime);
export const clearRuntimeRegistry   = _componentRuntime.clearRuntimeRegistry.bind(_componentRuntime);
export const mountComponent         = _componentRuntime.mountComponent.bind(_componentRuntime);
export const disposeComponent       = _componentRuntime.disposeComponent.bind(_componentRuntime);
export const handleEvent            = _componentRuntime.handleEvent.bind(_componentRuntime);
export const isComponentMounted     = _componentRuntime.isComponentMounted.bind(_componentRuntime);
export const getMountedComponentIds = _componentRuntime.getMountedComponentIds.bind(_componentRuntime);
